import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PublicLayout } from "@/components/PublicLayout";
import { CondoForm } from "@/components/LeadForms";
import { api, mediaUrl } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { CheckCircle2, ShieldCheck, Monitor, Clock, Ruler, HeartHandshake, CalendarClock, Gavel, FileText, BookOpen, ArrowRight } from "lucide-react";

const faqs = [
  {
    q: "Si può cambiare amministratore in qualsiasi momento dell'anno?",
    a: "Sì. L'assemblea può revocare e nominare un nuovo amministratore in qualunque periodo, anche a metà esercizio. Non è necessario attendere la scadenza del mandato: serve una delibera con le maggioranze di legge.",
  },
  {
    q: "Serve una maggioranza per cambiare amministratore?",
    a: "Per la nomina serve la maggioranza degli intervenuti in assemblea che rappresenti almeno la metà dei millesimi. Per chiedere invece la convocazione di un'assemblea straordinaria ad hoc bastano due condòmini che rappresentino un sesto (1/6) dei millesimi generali. Ti aiutiamo a verificare i numeri prima di muovere qualsiasi passo.",
  },
  {
    q: "Come ci si comporta con l'amministratore uscente?",
    a: "Con correttezza e spirito collaborativo. L'amministratore pro tempore resta in carica fino al subentro e per legge deve consegnare tutta la documentazione (registri, conti, contratti). Un rapporto costruttivo rende il passaggio più rapido e senza contenziosi: gestiamo noi la richiesta e il subentro.",
  },
  {
    q: "Quanto costa cambiare amministratore a Scandicci o Firenze?",
    a: "La nomina non ha costi occulti: ti forniamo un preventivo gratuito e trasparente, calcolato sul numero di unità e sui servizi richiesti. Nessuna sorpresa in fattura.",
  },
  {
    q: "Perché affidarsi a uno studio tecnico per l'amministrazione?",
    a: "Perché sul condominio abbiamo uno sguardo in più. Essere uno studio tecnico significa saper valutare lo stato dell'edificio, la necessità di manutenzioni e le pratiche edilizie con competenza diretta, seguendo con precisione ogni esigenza tecnica dello stabile.",
  },
];

const steps = [
  { n: "1", t: "Parliamone insieme", d: "Raccontaci la situazione del tuo condominio. Ti diamo un quadro chiaro di regole, tempi e possibilità, senza impegno." },
  { n: "2", t: "Verifichiamo la maggioranza", d: "Capiamo insieme se conviene attendere l'assemblea ordinaria o richiederne una straordinaria." },
  { n: "3", t: "Delibera di nomina", d: "L'assemblea vota revoca e nomina con le maggioranze di legge: ti supportiamo con tutta la modulistica." },
  { n: "4", t: "Passaggio di consegne", d: "Richiediamo la documentazione all'amministratore uscente e verifichiamo conti e contratti, in modo collaborativo." },
  { n: "5", t: "Subentro senza interruzioni", d: "Attiviamo il portale e prendiamo in carico la gestione: il condominio non resta mai scoperto." },
];

const perks = [
  { icon: ShieldCheck, t: "Trasparenza sui conti", d: "Contabilità leggibile, rendiconti puntuali e nessun costo nascosto: ogni spesa del condominio è tracciabile." },
  { icon: Monitor, t: "Portale per i condòmini", d: "Bilanci, documenti e comunicazioni accessibili online in ogni momento, non solo in assemblea." },
  { icon: Ruler, t: "Occhio tecnico sul condominio", d: "Siamo uno studio tecnico: valutiamo manutenzioni, lavori e pratiche edilizie con competenza diretta." },
  { icon: Clock, t: "Presenti quando serve", d: "Guasti e imprevisti seguiti con tempestività, con un referente chiaro a cui rivolgersi." },
];

const paths = [
  {
    icon: CalendarClock,
    t: "Attendere l'assemblea ordinaria",
    d: "La via più lineare: alla prossima assemblea annuale si inserisce all'ordine del giorno la revoca dell'amministratore uscente e la nomina del nuovo. Ideale quando non c'è una particolare urgenza.",
  },
  {
    icon: Gavel,
    t: "Richiedere un'assemblea straordinaria",
    d: "Quando si vuole agire subito, si può chiedere all'amministratore la convocazione di un'assemblea straordinaria per discutere revoca e nomina. La richiesta va sostenuta da almeno due condòmini che rappresentino un sesto (1/6) dei millesimi generali.",
  },
];

export default function CambioAmministratore() {
  const { data: allPosts = [] } = useQuery({ queryKey: ["posts"], queryFn: async () => (await api.get("/posts")).data });
  const guides = allPosts.filter((p) => p.category === "amministrazioni").slice(0, 3);

  useSeo({
    title: "Cambiare Amministratore di Condominio a Scandicci e Firenze | Edilstudio",
    description: "Vuoi cambiare amministratore di condominio a Scandicci o Firenze? Ti spieghiamo regole, maggioranze e tempi per farlo in modo sereno. Preventivo gratuito e trasparente.",
    canonicalPath: "/cambio-amministratore-condominio",
    image: "/img/cambio-amministratore.jpg",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "serviceType": "Cambio amministratore di condominio",
          "name": "Cambio amministratore di condominio a Scandicci e Firenze",
          "areaServed": [
            { "@type": "City", "name": "Scandicci" },
            { "@type": "City", "name": "Firenze" },
          ],
          "provider": {
            "@type": "RealEstateAgent",
            "name": "Edilstudio Immobiliare",
            "telephone": "+390555277501",
            "email": "info@edilstudiofi.it",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Via Salvador Allende 14/c",
              "addressLocality": "Scandicci",
              "addressRegion": "FI",
              "postalCode": "50018",
              "addressCountry": "IT",
            },
          },
        },
        {
          "@type": "FAQPage",
          "mainEntity": faqs.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        },
      ],
    },
  });

  return (
    <PublicLayout>
      {/* HERO + FORM */}
      <section className="grid lg:grid-cols-2" data-testid="cambio-hero">
        <div className="bg-[#1B2A41] text-white px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
          <p className="eyebrow text-[#F5A623]">Amministrazioni condominiali · Scandicci e Firenze</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold mt-3 leading-tight">
            Stai pensando di cambiare amministratore?
          </h1>
          <p className="text-white/80 mt-5 leading-relaxed max-w-md">
            Se sei arrivato fin qui, probabilmente con l'amministratore attuale qualcosa si è incrinato. È una situazione delicata, ma con l'approccio giusto il passaggio può avvenire in modo sereno e ordinato. Ti accompagniamo con chiarezza, un passo alla volta.
          </p>
          <ul className="mt-8 space-y-4">
            {["Ti spieghiamo regole, tempi e maggioranze", "Verifichiamo insieme il sostegno tra i condòmini", "Gestiamo il passaggio di consegne", "Nessuna interruzione nella gestione"].map((t) => (
              <li key={t} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-[#F5A623] mt-0.5 shrink-0" /><span className="text-white/85">{t}</span></li>
            ))}
          </ul>
        </div>
        <div className="px-6 sm:px-10 lg:px-16 py-16 lg:py-24 bg-[#F7FAFC]">
          <div className="bg-white rounded-2xl border border-[#DCE6EF] p-6 sm:p-8">
            <h2 className="font-display text-2xl font-bold text-[#1B2A41]">Parliamo del tuo condominio</h2>
            <p className="text-sm text-[#566476] mt-1 mb-6">Raccontaci la situazione: ti rispondiamo entro 24h con un confronto gratuito e un preventivo su misura.</p>
            <CondoForm />
          </div>
        </div>
      </section>

      {/* EMPATIA / FIDUCIA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24" data-testid="cambio-empathy">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#EEF4F9] flex items-center justify-center shrink-0"><HeartHandshake className="w-5 h-5 text-[#2472C8]" /></div>
              <p className="eyebrow text-[#2472C8]">Un momento delicato</p>
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#1B2A41] mt-4 leading-tight">Cambiare si può, ma conviene farlo con lucidità</h2>
            <div className="mt-6 space-y-4 text-[#43526A] leading-relaxed text-[17px]">
              <p>Quando la fiducia verso l'amministratore attuale viene meno — per comunicazioni poco chiare, conti difficili da leggere o risposte che tardano ad arrivare — è normale che nascano attriti e malintesi. Sono i momenti in cui è più facile scivolare in discussioni che non portano da nessuna parte.</p>
              <p>Il nostro primo consiglio, però, è proprio quello di <strong className="text-[#1B2A41]">rimanere costruttivi e collaborativi</strong>. L'amministratore pro tempore resta in carica fino al subentro e sarà lui ad accompagnare il condominio verso la nuova gestione: un rapporto sereno rende il passaggio più rapido, ordinato e senza contenziosi, a vantaggio di tutti i condòmini.</p>
            </div>
          </div>
          <div className="relative">
            <img src="/img/cambio-amministratore.jpg" alt="Condòmini che discutono insieme del cambio di amministratore a Scandicci e Firenze" loading="lazy" className="w-full aspect-[4/3] object-cover rounded-2xl shadow-lg" />
            <div className="absolute -bottom-5 -left-5 hidden sm:block bg-white rounded-2xl border border-[#DCE6EF] shadow-md px-5 py-4 max-w-[220px]">
              <p className="font-heading font-bold text-[#1B2A41] text-sm">Restare uniti conviene</p>
              <p className="text-xs text-[#566476] mt-1 leading-relaxed">La coesione tra condòmini è il primo passo verso una nuova gestione.</p>
            </div>
          </div>
        </div>
      </section>

      {/* POCHI O MAGGIORANZA */}
      <section className="bg-[#EEF4F9] py-16 lg:py-24" data-testid="cambio-maggioranza">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="eyebrow text-[#2472C8]">Il primo passo</p>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#1B2A41] mt-2 max-w-2xl">È la volontà di pochi o c'è già una maggioranza?</h2>
          <p className="text-[#43526A] mt-4 leading-relaxed max-w-3xl text-[17px]">Prima di muovere qualsiasi passo è utile capire il reale sostegno all'interno del condominio. Un conto è il malcontento di poche persone, un altro è una maggioranza già consolidata e decisa: da qui dipende la strada da percorrere.</p>
          <div className="grid md:grid-cols-2 gap-5 mt-10">
            {paths.map((p) => (
              <div key={p.t} className="bg-white rounded-2xl border border-[#DCE6EF] p-7">
                <div className="w-12 h-12 rounded-xl bg-[#EEF4F9] flex items-center justify-center"><p.icon className="w-6 h-6 text-[#2472C8]" /></div>
                <h3 className="font-heading font-bold text-[#1B2A41] mt-5 text-lg">{p.t}</h3>
                <p className="text-[#566476] mt-2 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
          <p className="text-[#43526A] mt-8 leading-relaxed max-w-3xl text-[17px]">Qualunque sia la via scelta, il segreto è restare <strong className="text-[#1B2A41]">caldi e decisi</strong>: determinazione e coesione tra i condòmini fanno la differenza. Noi ti aiutiamo a verificare i millesimi e a preparare la richiesta nel modo corretto.</p>
        </div>
      </section>

      {/* APPROCCIO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
        <p className="eyebrow text-[#2472C8]">Il nostro approccio</p>
        <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#1B2A41] mt-2 max-w-2xl">Una gestione trasparente, tecnica e vicina</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {perks.map((p) => (
            <div key={p.t} className="bg-white rounded-2xl border border-[#DCE6EF] p-6 hover:shadow-xl hover:border-[#2472C8]/40 transition-all">
              <div className="w-12 h-12 rounded-xl bg-[#EEF4F9] flex items-center justify-center"><p.icon className="w-6 h-6 text-[#2472C8]" /></div>
              <h3 className="font-heading font-bold text-[#1B2A41] mt-4">{p.t}</h3>
              <p className="text-sm text-[#566476] mt-2 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STEPS */}
      <section className="bg-[#EEF4F9] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="eyebrow text-[#2472C8]">Come ti accompagniamo</p>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#1B2A41] mt-2">Dal primo confronto al subentro, in 5 passi</h2>
          <div className="grid md:grid-cols-5 gap-5 mt-10">
            {steps.map((s) => (
              <div key={s.n} className="bg-white rounded-2xl border border-[#DCE6EF] p-6">
                <div className="w-10 h-10 rounded-full bg-[#F5A623] text-[#1B2A41] font-bold flex items-center justify-center font-mono">{s.n}</div>
                <h3 className="font-heading font-bold text-[#1B2A41] mt-4 text-[15px] leading-snug">{s.t}</h3>
                <p className="text-sm text-[#566476] mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
        <p className="eyebrow text-[#2472C8]">Domande frequenti</p>
        <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#1B2A41] mt-2">Cambio amministratore: i tuoi dubbi</h2>
        <div className="mt-8 space-y-3" data-testid="cambio-faq">
          {faqs.map((f, i) => (
            <details key={i} className="group bg-white rounded-2xl border border-[#DCE6EF] p-5 open:shadow-md transition-shadow" data-testid={`faq-item-${i}`}>
              <summary className="flex items-center justify-between cursor-pointer font-heading font-semibold text-[#1B2A41] list-none">
                <span className="flex items-start gap-3"><FileText className="w-5 h-5 text-[#2472C8] mt-0.5 shrink-0" />{f.q}</span>
                <ArrowRight className="w-5 h-5 text-[#8695A6] shrink-0 transition-transform group-open:rotate-90" />
              </summary>
              <p className="text-[#566476] mt-3 leading-relaxed pl-8">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* APPROFONDIMENTI / LINK INTERNI */}
      {guides.length > 0 && (
        <section className="bg-[#EEF4F9] py-16 lg:py-24" data-testid="cambio-guides">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <p className="eyebrow text-[#2472C8]">Approfondimenti</p>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#1B2A41] mt-2">Guide utili sulla gestione condominiale</h2>
            <div className="grid md:grid-cols-3 gap-6 mt-10">
              {guides.map((g) => (
                <Link key={g.slug} to={`/blog/${g.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-[#DCE6EF] hover:shadow-xl transition-shadow" data-testid={`cambio-guide-${g.slug}`}>
                  <div className="aspect-video overflow-hidden">
                    <img src={mediaUrl(g.cover_image)} alt={g.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-1.5 text-xs font-mono text-[#2472C8]"><BookOpen className="w-3.5 h-3.5" /> Blog</div>
                    <h3 className="font-heading font-semibold text-[#1B2A41] mt-2 leading-snug group-hover:text-[#2472C8] transition-colors line-clamp-3">{g.title}</h3>
                    <span className="inline-flex items-center gap-1 text-[#2472C8] font-semibold text-sm mt-4 group-hover:gap-2 transition-all">Leggi la guida <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA BAND */}
      <section className="bg-[#1B2A41] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-2xl lg:text-3xl font-bold">Non sai da dove iniziare? Parliamone.</h2>
            <p className="text-white/75 mt-2">Un confronto gratuito e senza impegno per capire la strada giusta per il tuo condominio a Scandicci e Firenze.</p>
          </div>
          <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="bg-[#F5A623] hover:bg-[#E0961C] text-[#1B2A41] font-bold px-7 py-4 rounded-full flex items-center gap-2 transition-colors whitespace-nowrap" data-testid="cambio-cta-top">
            Parliamo del tuo condominio <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>
    </PublicLayout>
  );
}
